import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { 
  User, 
  Save, 
  Mail, 
  Calendar, 
  Shield, 
  ShieldCheck, 
  ShieldOff,
  Smartphone,
  Trash2,
  AlertCircle,
  Check,
  X
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { getFirebaseAuth } from '@/lib/firebase'
import { 
  multiFactor, 
  PhoneAuthProvider, 
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  MultiFactorInfo
} from 'firebase/auth'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export function ProfilePage() {
  const { user } = useAuth()
  const [displayName, setDisplayName] = useState(user?.displayName || '')
  const [saving, setSaving] = useState(false)
  
  // MFA State
  const [mfaEnrolled, setMfaEnrolled] = useState(false)
  const [enrolledFactors, setEnrolledFactors] = useState<MultiFactorInfo[]>([])
  const [showMfaSetup, setShowMfaSetup] = useState(false)
  const [mfaStep, setMfaStep] = useState<'phone' | 'verify'>('phone')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [verificationId, setVerificationId] = useState<string | null>(null)
  const [mfaLoading, setMfaLoading] = useState(false)
  const [mfaError, setMfaError] = useState<string | null>(null)
  const [factorToRemove, setFactorToRemove] = useState<MultiFactorInfo | null>(null)
  const [recaptchaVerifier, setRecaptchaVerifier] = useState<RecaptchaVerifier | null>(null)

  // Check MFA status on mount
  useEffect(() => {
    if (user) {
      const mfaUser = multiFactor(user)
      const factors = mfaUser.enrolledFactors
      setEnrolledFactors(factors)
      setMfaEnrolled(factors.length > 0)
    }
  }, [user])

  // Initialize reCAPTCHA when MFA setup dialog opens
  useEffect(() => {
    if (showMfaSetup && !recaptchaVerifier) {
      const auth = getFirebaseAuth()
      const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'normal',
        callback: () => {
          // reCAPTCHA solved
        },
        'expired-callback': () => {
          setMfaError('reCAPTCHA expired. Please try again.')
        }
      })
      setRecaptchaVerifier(verifier)
    }
    
    // Cleanup reCAPTCHA when dialog closes
    if (!showMfaSetup && recaptchaVerifier) {
      recaptchaVerifier.clear()
      setRecaptchaVerifier(null)
    }
  }, [showMfaSetup])

  const handleSave = async () => {
    setSaving(true)
    setTimeout(() => setSaving(false), 1000)
  }

  const startMfaEnrollment = async () => {
    if (!user || !phoneNumber || !recaptchaVerifier) return
    
    setMfaLoading(true)
    setMfaError(null)
    
    try {
      const mfaUser = multiFactor(user)
      const session = await mfaUser.getSession()
      
      const phoneInfoOptions = {
        phoneNumber: phoneNumber,
        session: session
      }
      
      const phoneAuthProvider = new PhoneAuthProvider(getFirebaseAuth())
      const verId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
      
      setVerificationId(verId)
      setMfaStep('verify')
    } catch (error: any) {
      console.error('MFA enrollment error:', error)
      if (error.code === 'auth/requires-recent-login') {
        setMfaError('Please sign out and sign back in to enable MFA.')
      } else if (error.code === 'auth/invalid-phone-number') {
        setMfaError('Invalid phone number format. Please use +1XXXXXXXXXX format.')
      } else {
        setMfaError(error.message || 'Failed to start MFA enrollment')
      }
    } finally {
      setMfaLoading(false)
    }
  }

  const completeMfaEnrollment = async () => {
    if (!user || !verificationId || !verificationCode) return
    
    setMfaLoading(true)
    setMfaError(null)
    
    try {
      const cred = PhoneAuthProvider.credential(verificationId, verificationCode)
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)
      
      const mfaUser = multiFactor(user)
      await mfaUser.enroll(multiFactorAssertion, 'Phone Number')
      
      // Refresh enrolled factors
      const factors = mfaUser.enrolledFactors
      setEnrolledFactors(factors)
      setMfaEnrolled(factors.length > 0)
      
      // Reset and close dialog
      setShowMfaSetup(false)
      setMfaStep('phone')
      setPhoneNumber('')
      setVerificationCode('')
      setVerificationId(null)
    } catch (error: any) {
      console.error('MFA verification error:', error)
      if (error.code === 'auth/invalid-verification-code') {
        setMfaError('Invalid verification code. Please try again.')
      } else {
        setMfaError(error.message || 'Failed to complete MFA enrollment')
      }
    } finally {
      setMfaLoading(false)
    }
  }

  const removeMfaFactor = async () => {
    if (!user || !factorToRemove) return
    
    setMfaLoading(true)
    
    try {
      const mfaUser = multiFactor(user)
      await mfaUser.unenroll(factorToRemove)
      
      // Refresh enrolled factors
      const factors = mfaUser.enrolledFactors
      setEnrolledFactors(factors)
      setMfaEnrolled(factors.length > 0)
      setFactorToRemove(null)
    } catch (error: any) {
      console.error('MFA removal error:', error)
      if (error.code === 'auth/requires-recent-login') {
        setMfaError('Please sign out and sign back in to remove MFA.')
      }
    } finally {
      setMfaLoading(false)
    }
  }

  const closeMfaSetup = () => {
    setShowMfaSetup(false)
    setMfaStep('phone')
    setPhoneNumber('')
    setVerificationCode('')
    setVerificationId(null)
    setMfaError(null)
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Please log in to view your profile</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <User className="h-8 w-8" />
        <div>
          <h1 className="text-3xl font-bold">Profile</h1>
          <p className="text-muted-foreground">
            Manage your account settings
          </p>
        </div>
      </div>

      {/* Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle>Your Profile</CardTitle>
          <CardDescription>
            Your personal information and account details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar Section */}
          <div className="flex items-center space-x-4">
            <Avatar className="h-20 w-20">
              <AvatarImage src={user.photoURL || ''} alt={user.displayName || ''} />
              <AvatarFallback className="text-2xl">
                {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-semibold">{user.displayName || 'User'}</h3>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>

          {/* Form */}
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your display name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  value={user.email || ''}
                  disabled
                  className="bg-muted"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Email cannot be changed. Contact support if you need to update it.
              </p>
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
          <CardDescription>
            Details about your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Account Created</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {user.metadata?.creationTime 
                  ? new Date(user.metadata.creationTime).toLocaleDateString()
                  : 'Unknown'}
              </span>
            </div>
            
            <div className="flex items-center justify-between py-2 border-b">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Last Sign In</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {user.metadata?.lastSignInTime 
                  ? new Date(user.metadata.lastSignInTime).toLocaleDateString()
                  : 'Unknown'}
              </span>
            </div>
            
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">User ID</span>
              </div>
              <span className="text-sm font-mono text-muted-foreground">
                {user.uid.substring(0, 16)}...
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security / MFA Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security
              </CardTitle>
              <CardDescription>
                Manage your account security settings
              </CardDescription>
            </div>
            {mfaEnrolled ? (
              <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                <ShieldCheck className="h-3 w-3 mr-1" />
                MFA Enabled
              </Badge>
            ) : (
              <Badge variant="outline" className="text-amber-600 border-amber-500/20">
                <ShieldOff className="h-3 w-3 mr-1" />
                MFA Disabled
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-medium mb-2">Two-Factor Authentication (2FA)</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Add an extra layer of security to your account by requiring a verification code from your phone when signing in.
            </p>
            
            {mfaError && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-destructive/10 border border-destructive/20 rounded-md">
                <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                <span className="text-sm text-destructive">{mfaError}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="ml-auto h-6 w-6 p-0"
                  onClick={() => setMfaError(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            {enrolledFactors.length > 0 ? (
              <div className="space-y-3">
                <h5 className="text-sm font-medium">Enrolled Methods</h5>
                {enrolledFactors.map((factor) => (
                  <div 
                    key={factor.uid} 
                    className="flex items-center justify-between p-3 bg-background border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Smartphone className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium">{factor.displayName || 'Phone'}</div>
                        <div className="text-xs text-muted-foreground">
                          Enrolled {factor.enrollmentTime ? new Date(factor.enrollmentTime).toLocaleDateString() : 'Unknown'}
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setFactorToRemove(factor)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button 
                  variant="outline" 
                  className="w-full mt-2"
                  onClick={() => setShowMfaSetup(true)}
                >
                  <Smartphone className="h-4 w-4 mr-2" />
                  Add Another Phone
                </Button>
              </div>
            ) : (
              <Button onClick={() => setShowMfaSetup(true)}>
                <Shield className="h-4 w-4 mr-2" />
                Enable Two-Factor Authentication
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription>
            Irreversible account actions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 border border-destructive/20 rounded-lg">
            <div>
              <div className="font-medium">Delete Account</div>
              <div className="text-sm text-muted-foreground">
                Permanently delete your account and all associated data
              </div>
            </div>
            <Button variant="destructive" disabled>
              Delete Account
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* MFA Setup Dialog */}
      <Dialog open={showMfaSetup} onOpenChange={(open) => !open && closeMfaSetup()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Set Up Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              {mfaStep === 'phone' 
                ? 'Enter your phone number to receive verification codes when signing in.'
                : 'Enter the verification code sent to your phone.'}
            </DialogDescription>
          </DialogHeader>
          
          {mfaStep === 'phone' ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Enter your phone number with country code (e.g., +1 for US)
                </p>
              </div>
              
              {/* reCAPTCHA container */}
              <div id="recaptcha-container" className="flex justify-center"></div>
              
              {mfaError && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                  <span className="text-sm text-destructive">{mfaError}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Verification Code</Label>
                <Input
                  id="code"
                  type="text"
                  placeholder="123456"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  maxLength={6}
                  className="text-center text-2xl tracking-widest"
                />
                <p className="text-xs text-muted-foreground">
                  Enter the 6-digit code sent to {phoneNumber}
                </p>
              </div>
              
              {mfaError && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                  <span className="text-sm text-destructive">{mfaError}</span>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={closeMfaSetup}>
              Cancel
            </Button>
            {mfaStep === 'phone' ? (
              <Button 
                onClick={startMfaEnrollment} 
                disabled={mfaLoading || !phoneNumber || !recaptchaVerifier}
              >
                {mfaLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  'Send Code'
                )}
              </Button>
            ) : (
              <Button 
                onClick={completeMfaEnrollment} 
                disabled={mfaLoading || verificationCode.length !== 6}
              >
                {mfaLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Verify & Enable
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove MFA Confirmation Dialog */}
      <AlertDialog open={!!factorToRemove} onOpenChange={(open) => !open && setFactorToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Two-Factor Authentication?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {factorToRemove?.displayName || 'this phone'} from your account. 
              You will no longer need to verify with this method when signing in.
              {enrolledFactors.length === 1 && (
                <span className="block mt-2 text-amber-600 font-medium">
                  Warning: This is your only 2FA method. Your account will no longer be protected by two-factor authentication.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={removeMfaFactor}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {mfaLoading ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
